import React, { use, useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Dialog,
  Slide,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
  InputAdornment,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';
import Cards from 'react-credit-cards-2';
import 'react-credit-cards-2/dist/es/styles-compiled.css';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import dayjs, { Dayjs } from 'dayjs';
import { SubscriptionService } from '../../services/asaas/AsaasSubscriptionService';
import { Plan, plans } from '../../interfaces/Plan';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface PaymentFullScreenModalProps {
  open: boolean;
  onClose: () => void;
  placeId: number;
  selectedPlan: Plan | null;
}

export default function PaymentFullScreenModal({
  open,
  onClose,
  placeId,
  selectedPlan,
}: PaymentFullScreenModalProps) {
  const [activeStep, setActiveStep] = useState(1);
  const [thanksGivingLoad, setThanksGivingLoad] = useState(false);
  const [invoiceURL, setInvoiceURL] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    creditCard: {
      holderName: null as string | null,
      number: null as string | null,
      expiryMonth: null as string | null,
      expiryYear: null as string | null,
      ccv: null as string | null,
    },
    creditCardHolderInfo: {
      name: null as string | null,
      email: null as string | null,
      cpfCnpj: null as string | null,
      postalCode: null as string | null,
      addressNumber: null as string | null,
      addressComplement: null as string | null,
      phone: null as string | null,
      mobilePhone: null as string | null,
    },
    nextDueDate: null as Dayjs | null,
    placeId: placeId,
    plan: selectedPlan as Plan,
    planType: selectedPlan?.type as string | null,
    customAmount: null as number | null,
  });

  const [focused, setFocused] = useState<string>('');
  const [customAmountInput, setCustomAmountInput] = useState<string>('');

  const handleChange =
    (section: 'creditCard' | 'creditCardHolderInfo' | 'placeId' | 'plan', field?: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (section === 'placeId') {
        setFormData({ ...formData, placeId: Number(value) });
      } else if (section === 'plan') {
        setFormData({ ...formData, planType: formData.plan.type });
      } else {
        setFormData({
          ...formData,
          [section]: { ...formData[section], [field!]: value },
        });
      }
    };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  const formatCurrencyInput = (value: number) =>
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      value,
    );

  const parseCurrencyToNumber = (raw: string) => {
    if (!raw) return null;
    const normalized = raw
      .replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    const value = Number(normalized);
    return Number.isNaN(value) ? null : value;
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setCustomAmountInput(raw);
    const value = parseCurrencyToNumber(raw);
    setFormData({ ...formData, customAmount: value });
  };

  const handleCustomAmountBlur = () => {
    if (!customAmountInput) return;
    const value = parseCurrencyToNumber(customAmountInput);
    if (value === null) return;
    setCustomAmountInput(formatCurrencyInput(value));
  };

  const handleFocus = (field: string) => () => setFocused(field);

  const handleSubmitPaymentModal = (data: AsaasSubscription) => {
    SubscriptionService.create(data)
      .then((response) => {
        setInvoiceURL(response || null);
        setThanksGivingLoad(true);

        if (formData.plan?.type !== 'CREDIT_CARD') window.open(response, '_blank');
      })
      .catch((error: any) => {
        alert('Erro ao criar assinatura: ' + error?.response?.data?.details['0'].description);
      });
  };

  useEffect(() => {
    if (!selectedPlan || !selectedPlan.name) {
      setActiveStep(0);
    }
  }, []);

  const onCloseModal = () => {
    clearForm();
    onClose();
  };

  const handleSubmit = () => {
    handleSubmitPaymentModal({
      ...formData,
      nextDueDate: formData.nextDueDate?.toDate() || new Date(),
    });
  };
  const isPrepaid = formData.plan?.type === 'PREPAID';
  const planFromList = plans.find(
    (p) =>
      p.id === formData.plan?.id ||
      p.type === formData.plan?.type ||
      p.name === formData.plan?.name,
  );
  const minCustomAmount = formData.plan?.minValue ?? planFromList?.minValue ?? null;
  const isCustomAmountValid =
    !isPrepaid ||
    formData.customAmount === null ||
    (minCustomAmount !== null ? formData.customAmount >= minCustomAmount : true);
  const { creditCard } = formData;
  const expiry = `${creditCard.expiryMonth}${creditCard?.expiryYear?.slice(-2)}`;

  const clearForm = () => {
    setInvoiceURL(null);
    setThanksGivingLoad(false);
    setFormData({
      creditCard: {
        holderName: null as string | null,
        number: null as string | null,
        expiryMonth: null as string | null,
        expiryYear: null as string | null,
        ccv: null as string | null,
      },
      creditCardHolderInfo: {
        name: null as string | null,
        email: null as string | null,
        cpfCnpj: null as string | null,
        postalCode: null as string | null,
        addressNumber: null as string | null,
        addressComplement: null as string | null,
        phone: null as string | null,
        mobilePhone: null as string | null,
      },
      nextDueDate: null as Dayjs | null,
      placeId: placeId,
      planType: null as string | null,
      customAmount: null as number | null,
    });
    setCustomAmountInput('');
    setActiveStep(1);
  };

  return (
    <Dialog fullScreen open={open} onClose={onCloseModal} TransitionComponent={Transition}>
      <Box sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onCloseModal}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </Box>

      {thanksGivingLoad ? (
        <Box sx={{ p: 3, maxWidth: 800, margin: 'auto', textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Obrigado por assinar um de nossos planos!
          </Typography>
          <Typography variant="body1" gutterBottom>
            Após o pagamento ser confirmado, seu espaço será ativado. Você pode acompanhar o status
            da sua assinatura através do e-mail cadastrado.
          </Typography>
          <Box mt={4} display="flex" justifyContent="space-between" gap={2}>
            <Button variant="outlined" sx={{ mt: 2, ml: 2 }} onClick={onCloseModal}>
              Fechar
            </Button>
            {invoiceURL && formData.plan?.type !== 'CREDIT_CARD' && (
              <Button
                variant="contained"
                color="primary"
                href={invoiceURL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 2 }}
              >
                Visualizar Fatura
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1000, margin: 'auto' }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            <Step>
              <StepLabel>Escolha do Plano</StepLabel>
            </Step>
            <Step>
              <StepLabel>Dados do Pagamento</StepLabel>
            </Step>
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={2}>
                Selecione o plano desejado
              </Typography>

              {/* Cards using Box instead of Grid */}
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                {plans.map((plan) => {
                  const selected = formData.plan?.id === plan.id;

                  return (
                    <Box key={plan.id} mt={1} width="100%">
                      <Paper
                        sx={{
                          p: 2.5,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          border: selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          boxShadow: selected ? '0 10px 30px rgba(25, 118, 210, 0.15)' : 'none',
                          transition: '0.25s',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#1976d2',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                          },
                        }}
                        onClick={() => {
                          setFormData({ ...formData, plan: plan, planType: plan.type });
                          setActiveStep(1);
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: selected ? 'primary.50' : 'grey.50',
                              border: '1px solid',
                              borderColor: selected ? 'primary.100' : 'grey.200',
                              mb: 1.5,
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold">
                              {plan.name}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                              {plan.value}
                            </Typography>
                          </Box>

                          <Typography
                            variant="body2"
                            mt={1}
                            component="div"
                            sx={{
                              textAlign: 'left',
                              lineHeight: 1.6,
                              '& p': { margin: 0, marginBottom: 1.25 },
                              '& p:last-of-type': { marginBottom: 0 },
                            }}
                            dangerouslySetInnerHTML={{ __html: plan.description }}
                          />

                          <Divider sx={{ my: 2 }} />

                          <Box mt={1}>
                            <Typography fontWeight="bold">Benefícios:</Typography>
                            {plan.benefits.map((b, i) => (
                              <Typography key={i} variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                                {b}
                              </Typography>
                            ))}
                          </Box>
                        </Box>

                        <Box mt={2}>
                          <Button fullWidth variant={selected ? 'contained' : 'outlined'}>
                            {selected ? 'Selecionado' : 'Selecionar'}
                          </Button>
                        </Box>
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {activeStep === 1 && formData.plan?.type !== 'CREDIT_CARD' && (
            <Box mt={3} display="flex" flexDirection="column" gap={2}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, width: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Plano selecionado
                  </Typography>
                  <Button size="small" variant="text" onClick={() => setActiveStep(0)}>
                    Trocar plano
                  </Button>
                </Box>
                {formData.plan && (
                  <>
                    <Typography variant="body1" fontWeight="bold">
                      {formData.plan?.name}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" mt={0.5}>
                      {formData.plan?.value}
                    </Typography>

                    <Box
                      mt={2}
                      display="grid"
                      gridTemplateColumns={{ xs: '1fr', md: '1.2fr 1fr' }}
                      gap={{ xs: 1.5, md: 2 }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          component="div"
                          dangerouslySetInnerHTML={{ __html: formData.plan.description }}
                        />
                      </Box>
                      <Box>
                        <Typography fontWeight="bold">Benefícios:</Typography>
                        {formData.plan?.benefits.map((b, i) => (
                          <Typography key={i} variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            {b}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}

                {isPrepaid && (
                  <Box mt={2} maxWidth={400}>
                    <TextField
                      label={
                        minCustomAmount !== null
                          ? `Valor do aporte (mínimo ${formatCurrency(minCustomAmount)})`
                          : 'Valor do aporte'
                      }
                      type="text"
                      fullWidth
                      variant="standard"
                      value={customAmountInput}
                      onChange={handleCustomAmountChange}
                      onBlur={handleCustomAmountBlur}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                      helperText={
                        !isCustomAmountValid && minCustomAmount !== null
                          ? `Informe um valor igual ou maior que ${formatCurrency(
                              minCustomAmount,
                            )}.`
                          : 'Opcional: preencha apenas se quiser definir um novo valor de aporte.'
                      }
                      error={!isCustomAmountValid}
                    />
                  </Box>
                )}
              </Paper>

              {formData.plan && formData.plan.type !== 'FREE' && (
                <Paper
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Pagamento
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Para concluir a contratação do plano {formData.plan?.name}, será gerada uma
                    cobrança para o e-mail do responsável e os demais dados cadastrados no espaço.
                    Por favor, siga as instruções enviadas para o e-mail ou prossiga para ser
                    encaminhado para tela de cobrança.
                  </Typography>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={!isCustomAmountValid}
                    >
                      Gerar Cobrança
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {activeStep === 1 && formData.plan?.type === 'CREDIT_CARD' && (
            <Box mt={3} display="flex" flexDirection="column" gap={2}>
              <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, width: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Plano selecionado
                  </Typography>
                  <Button size="small" variant="text" onClick={() => setActiveStep(0)}>
                    Trocar plano
                  </Button>
                </Box>
                {formData.plan && (
                  <>
                    <Typography variant="body1" fontWeight="bold">
                      {formData.plan.name}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" mt={0.5}>
                      {formData.plan.value}
                    </Typography>

                    <Box
                      mt={2}
                      display="grid"
                      gridTemplateColumns={{ xs: '1fr', md: '1.2fr 1fr' }}
                      gap={{ xs: 1.5, md: 2 }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          component="div"
                          dangerouslySetInnerHTML={{ __html: formData.plan?.description || '' }}
                        />
                      </Box>
                      <Box>
                        <Typography fontWeight="bold">Benefícios:</Typography>
                        {formData.plan?.benefits.map((b, i) => (
                          <Typography key={i} variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            {b}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}

                {isPrepaid && (
                  <Box mt={2}>
                    <TextField
                      label={
                        minCustomAmount !== null
                          ? `Valor do aporte (mínimo ${formatCurrency(minCustomAmount)})`
                          : 'Valor do aporte'
                      }
                      type="text"
                      fullWidth
                      variant="standard"
                      value={customAmountInput}
                      onChange={handleCustomAmountChange}
                      onBlur={handleCustomAmountBlur}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                      helperText={
                        !isCustomAmountValid && minCustomAmount !== null
                          ? `Informe um valor igual ou maior que ${formatCurrency(
                              minCustomAmount,
                            )}.`
                          : 'Opcional: preencha apenas se quiser definir um novo valor de aporte.'
                      }
                      error={!isCustomAmountValid}
                    />
                  </Box>
                )}
              </Paper>

              <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, width: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Dados do Cartão
                </Typography>
                <Box
                  sx={{
                    transform: { xs: 'scale(0.9)', sm: 'none' },
                    transformOrigin: 'center left',
                    width: '100%',
                  }}
                >
                  <Cards
                    number={creditCard?.number}
                    name={creditCard?.holderName}
                    expiry={expiry}
                    cvc={creditCard?.ccv}
                    focused={focused}
                  />
                </Box>

                <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                  <Box flex={{ xs: '1 1 100%', md: '1 1 48%' }}>
                    <TextField
                      label="Nome impresso no cartão"
                      fullWidth
                      required
                      variant="standard"
                      value={creditCard.holderName}
                      onChange={handleChange('creditCard', 'holderName')}
                      onFocus={handleFocus('name')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 100%', md: '1 1 48%' }}>
                    <TextField
                      label="Número do cartão"
                      fullWidth
                      required
                      variant="standard"
                      value={creditCard.number}
                      onChange={handleChange('creditCard', 'number')}
                      onFocus={handleFocus('number')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <FormControl fullWidth required variant="standard">
                      <InputLabel>Mês</InputLabel>
                      <Select
                        value={creditCard.expiryMonth}
                        label="Mês"
                        onChange={handleChange('creditCard', 'expiryMonth')}
                        onFocus={handleFocus('expiry')}
                      >
                        {Array.from({ length: 12 }, (_, i) => {
                          const month = (i + 1).toString().padStart(2, '0');
                          return (
                            <MenuItem key={month} value={month}>
                              {month}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        views={['year']}
                        label="Ano"
                        minDate={dayjs()}
                        value={
                          creditCard.expiryYear ? dayjs().year(Number(creditCard.expiryYear)) : null
                        }
                        onChange={(newValue: Dayjs | null) => {
                          if (newValue) {
                            setFormData((prev) => ({
                              ...prev,
                              creditCard: {
                                ...prev.creditCard,
                                expiryYear: newValue.year().toString(),
                              },
                            }));
                          }
                        }}
                        slotProps={{
                          textField: { variant: 'standard', fullWidth: true, required: true },
                        }}
                        renderInput={(params: any) => (
                          <TextField {...params} fullWidth required variant="standard" />
                        )}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <TextField
                      label="CCV"
                      fullWidth
                      required
                      value={creditCard.ccv}
                      variant="standard"
                      onChange={handleChange('creditCard', 'ccv')}
                      onFocus={handleFocus('cvc')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={'Data de Vencimento da Fatura'}
                        value={formData.nextDueDate}
                        minDate={dayjs()}
                        format="DD/MM/YYYY"
                        views={['day', 'month', 'year']}
                        onChange={(newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            nextDueDate: newValue,
                          }));
                        }}
                        slotProps={{
                          textField: { variant: 'standard', fullWidth: true, required: true },
                        }}
                        renderInput={(params: any) => (
                          <TextField {...params} fullWidth variant="standard" required />
                        )}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Dados do Titular
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2}>
                  <Box flex={{ xs: '1 1 100%', md: '1 1 48%' }}>
                    <TextField
                      label="Nome completo"
                      fullWidth
                      required
                      variant="standard"
                      value={formData.creditCardHolderInfo.name}
                      onChange={handleChange('creditCardHolderInfo', 'name')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 100%', md: '1 1 48%' }}>
                    <TextField
                      label="E-mail"
                      type="email"
                      fullWidth
                      required
                      variant="standard"
                      value={formData.creditCardHolderInfo.email}
                      onChange={handleChange('creditCardHolderInfo', 'email')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 100%', md: '1 1 48%' }}>
                    <TextField
                      label="CPF/CNPJ"
                      fullWidth
                      required
                      variant="standard"
                      value={formData.creditCardHolderInfo.cpfCnpj}
                      onChange={handleChange('creditCardHolderInfo', 'cpfCnpj')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 100%', md: '1 1 48%' }}>
                    <TextField
                      label="CEP"
                      fullWidth
                      required
                      variant="standard"
                      value={formData.creditCardHolderInfo.postalCode}
                      onChange={handleChange('creditCardHolderInfo', 'postalCode')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <TextField
                      label="Número"
                      fullWidth
                      required
                      variant="standard"
                      value={formData.creditCardHolderInfo.addressNumber}
                      onChange={handleChange('creditCardHolderInfo', 'addressNumber')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <TextField
                      label="Complemento"
                      fullWidth
                      variant="standard"
                      value={formData.creditCardHolderInfo.addressComplement ?? ''}
                      onChange={handleChange('creditCardHolderInfo', 'addressComplement')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <TextField
                      label="Telefone"
                      fullWidth
                      required
                      variant="standard"
                      value={formData.creditCardHolderInfo.phone}
                      onChange={handleChange('creditCardHolderInfo', 'phone')}
                    />
                  </Box>
                  <Box flex={{ xs: '1 1 40%', md: '1 1 20%' }}>
                    <TextField
                      label="Celular"
                      fullWidth
                      variant="standard"
                      value={formData.creditCardHolderInfo.mobilePhone}
                      onChange={handleChange('creditCardHolderInfo', 'mobilePhone')}
                    />
                  </Box>
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!isCustomAmountValid}
                  >
                    Confirmar Pagamento
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
}
